import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronLeft, 
  faChevronRight, 
  faCalendar,
  faUser,
  faClock,
 
} from '@fortawesome/free-solid-svg-icons';
import Topnavbar from '../Header-pages/Topnavbar';
import Navbar from '../Header-pages/Navbar';
import Footer from '../Header-pages/Footer';
import { API_URL } from '../utils/Function';
import '../Css/BlogsDetails.css';
import { Helmet } from "react-helmet-async";

export default function BlogsDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('content');

  // Fetch blog details by slug
  const fetchBlogDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/blog.php/get`);
      const data = await response.json();
      
      if (data.success) {
        // Find blog by slug
        const foundBlog = data.blogs.find(b => b.slug === slug);
        
        if (foundBlog) {
          setBlog(foundBlog);
          
          // Fetch author details if author_id exists
          if (foundBlog.author_id) {
            await fetchAuthorDetails(foundBlog.author_id);
          }
        } else {
          setError('Blog not found');
        }
      } else {
        setError(data.error || 'Failed to fetch blog');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // Fetch author details
  const fetchAuthorDetails = async (authorId) => {
    try {
      const response = await fetch(`${API_URL}/user.php/get`);
      const data = await response.json();
      
      if (data.success) {
        const foundAuthor = data.users.find(user => user.id === authorId);
        if (foundAuthor) {
          setAuthor(foundAuthor);
        }
      }
    } catch (error) {
      console.error('Error fetching author details:', error);
    }
  };

  // Get author image URL or return null for icon fallback
  const getAuthorImage = () => {
    if (!author) return null;
    
    if (author.image_url) {
      return `${API_URL}/${author.image_url}`;
    } else if (author.image) {
      return `${API_URL}/${author.image}`;
    } else {
      return null;
    }
  };

  // Get author name
  const getAuthorName = () => {
    if (author && author.name) {
      return author.name;
    }
    return blog?.author_name || 'Unknown Author';
  };

  // Get author bio
  const getAuthorBio = () => {
    if (author && author.bio) {
      return author.bio;
    }
    return 'Content writer and technology enthusiast.';
  };

  useEffect(() => {
    if (slug) {
      fetchBlogDetails();
    }
  }, [slug, fetchBlogDetails]);

  // Auto slide functionality for gallery
  useEffect(() => {
    if (!blog?.gallery_images_urls?.length) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => 
        prev === blog.gallery_images_urls.length - 1 ? 0 : prev + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [blog?.gallery_images_urls]);

  const nextImage = () => {
    if (blog?.gallery_images_urls) {
      setCurrentImageIndex(prev => 
        prev === blog.gallery_images_urls.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (blog?.gallery_images_urls) {
      setCurrentImageIndex(prev => 
        prev === 0 ? blog.gallery_images_urls.length - 1 : prev - 1
      );
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <>
        <Topnavbar />
        <Navbar />
        <div className="blog-details-loading">Loading blog details...</div>
        <Footer />
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <Topnavbar />
        <Navbar />
        <div className="blog-details-error">
          <p>{error || 'Blog not found'}</p>
          <button onClick={() => navigate('/')} className="back-home-btn">
            Back to Home
          </button>
        </div>
        <Footer />
      </>
    );
  }

  const images = blog.gallery_images_urls || [];
  const hasGallery = images.length > 0;
  const mainImage = hasGallery ? images[currentImageIndex] : blog.featured_image_url;

  // Calculate read time
  const calculateReadTime = (content) => {
    if (!content) return '2 min read';
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
    <Helmet>
        <title>
          {blog?.meta_title 
            ? blog.meta_title 
            : `${blog?.title} | SAFPRO Technology Solutions`}
        </title>
        <meta 
          name="description" 
          content={blog?.meta_description || blog?.excerpt || blog?.title} 
        />
      </Helmet>
      <Topnavbar />
      <Navbar />
      
      <div className="blog-details-page">
        {/* Hero Section */}
        <div 
          className="blog-details-hero"
          style={{
            backgroundImage: `url("/images/Productview.jpg")`,
          }}
        >
          <div className="blog-details-hero-overlay">
            <div className="blog-details-container">
              {/* Breadcrumbs */}
              <div className="blog-details-breadcrumbs">
                <a onClick={()=>navigate('/')} style={{cursor:'pointer'}} className="blog-details-breadcrumb-link">Home</a>
                <span className="blog-details-breadcrumb-separator">/</span>
                <a onClick={()=>navigate('/blogs')} style={{cursor:'pointer'}} className="blog-details-breadcrumb-link">Blogs</a>
                <span className="blog-details-breadcrumb-separator">/</span>
                <span className="blog-details-breadcrumb-current">{blog.title}</span>
              </div>

              {/* Blog Title and Meta */}
              <div className="blog-details-header">
                <h1 className="blog-details-title">{blog.title}</h1>
                {/* <p className="blog-details-excerpt">{blog.excerpt}</p> */}
                
                <div className="blog-details-meta">
                  <div className="blog-details-author">
                    {getAuthorImage() ? (
                      <img
                        src={getAuthorImage()}
                        alt={getAuthorName()}
                        className="blog-details-author-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="blog-details-author-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    )}
                    {!getAuthorImage() && (
                      <div className="blog-details-author-icon" style={{display: 'flex'}}>
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    )}
                    <div className="blog-details-author-info">
                      <span className="blog-details-author-name">{getAuthorName()}</span>
                      <div className="blog-details-meta-details">
                        <span className="blog-details-date">
                          <FontAwesomeIcon icon={faCalendar} />
                          {formatDate(blog.published_at || blog.created_at)}
                        </span>
                        <span className="blog-details-read-time">
                          <FontAwesomeIcon icon={faClock} />
                          {calculateReadTime(blog.content)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* <div className="blog-details-actions">
                    <button className="blog-details-action-btn">
                      <FontAwesomeIcon icon={faBookmark} />
                    </button>
                    <button className="blog-details-action-btn">
                      <FontAwesomeIcon icon={faShareAlt} />
                    </button>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="blog-details-content">
          <div className="blog-details-container">
            <div className="blog-details-layout">
              {/* Left Column - Content */}
              <div className="blog-details-main-content">
                {/* Featured Image or Gallery */}
                <div className="blog-details-gallery-card">
                  <div className="blog-details-main-image">
                    {mainImage ? (
                      <img 
                        src={`${API_URL}/${mainImage}`} 
                        alt={blog.title}
                        className="blog-details-main-image-img"
                        onError={(e) => {
                          e.target.src = '/images/default-blog.png';
                        }}
                      />
                    ) : (
                      <div className="blog-details-image-placeholder">
                        <span>No Image Available</span>
                      </div>
                    )}
                    
                    {/* Navigation Arrows */}
                    {hasGallery && images.length > 1 && (
                      <>
                        <button 
                          className="blog-details-arrow blog-details-arrow-prev"
                          onClick={prevImage}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button 
                          className="blog-details-arrow blog-details-arrow-next"
                          onClick={nextImage}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Thumbnail Gallery */}
                  {hasGallery && images.length > 1 && (
                    <div className="blog-details-thumbnails">
                      {images.map((image, index) => (
                        <button
                          key={index}
                          className={`blog-details-thumbnail ${
                            index === currentImageIndex ? 'blog-details-thumbnail-active' : ''
                          }`}
                          onClick={() => goToImage(index)}
                        >
                          <img 
                            src={`${API_URL}/${image}`} 
                            alt={`Gallery ${index + 1}`}
                            className="blog-details-thumbnail-img"
                            onError={(e) => {
                              e.target.src = '/images/default-blog.png';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tabs */}
                <div className="blog-details-tabs">
                  <button 
                    className={`blog-details-tab ${
                      activeTab === 'content' ? 'blog-details-tab-active' : ''
                    }`}
                    onClick={() => setActiveTab('content')}
                  >
                    Content
                  </button>
                  <button 
                    className={`blog-details-tab ${
                      activeTab === 'details' ? 'blog-details-tab-active' : ''
                    }`}
                    onClick={() => setActiveTab('details')}
                  >
                    Details
                  </button>
                </div>

                {/* Tab Content */}
                <div className="blog-details-tab-content">
                  {activeTab === 'content' && (
                    <div className="blog-details-article-content">
                      <div 
                        className="blog-details-content-html"
                        dangerouslySetInnerHTML={{ __html: blog.content }}
                      />
                    </div>
                  )}

                  {activeTab === 'details' && (
                    <div className="blog-details-additional-info">
                      <h3 className="blog-details-section-title">Blog Information</h3>
                      <div className="blog-details-info-grid">
                        <div className="blog-details-info-item">
                          <strong>Category:</strong>
                          <span>{blog.category_name || 'Uncategorized'}</span>
                        </div>
                        <div className="blog-details-info-item">
                          <strong>Published:</strong>
                          <span>{formatDate(blog.published_at || blog.created_at)}</span>
                        </div>
                        <div className="blog-details-info-item">
                          <strong>Author:</strong>
                          <span>{getAuthorName()}</span>
                        </div>
                        <div className="blog-details-info-item">
                          <strong>Read Time:</strong>
                          <span>{calculateReadTime(blog.content)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Sidebar */}
              <div className="blog-details-sidebar">
                {/* Author Card */}
                <div className="blog-details-sidebar-card">
                  <h3 className="blog-details-sidebar-title">About the Author</h3>
                  <div className="blog-details-author-card">
                    {getAuthorImage() ? (
                      <img 
                        src={getAuthorImage()} 
                        alt={getAuthorName()}
                        className="blog-details-author-card-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="blog-details-author-card-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    )}
                    {!getAuthorImage() && (
                      <div className="blog-details-author-card-icon" style={{display: 'flex'}}>
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    )}
                    <div className="blog-details-author-card-info">
                      <h4>{getAuthorName()}</h4>
                      <p>{getAuthorBio()}</p>
                    </div>
                  </div>
                </div>

                {/* Related Posts */}
                <div className="blog-details-sidebar-card">
                  <h3 className="blog-details-sidebar-title">Related Posts</h3>
                  <div className="blog-details-related-posts">
                    {/* You can fetch and display related posts here */}
                    <p>More blogs coming soon...</p>
                  </div>
                </div>

                {/* Share Card */}
                <div className="blog-details-sidebar-card">
                  <h3 className="blog-details-sidebar-title">Share This Post</h3>
                  <div className="blog-details-share-buttons">
                    <button className="blog-details-share-btn blog-details-share-twitter">Twitter</button>
                    <button className="blog-details-share-btn blog-details-share-linkedin">LinkedIn</button>
                    <button className="blog-details-share-btn blog-details-share-facebook">Facebook</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}